import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import createOpenApiClient from 'openapi-react-query';
import { createApiClient } from '../api/client';
import { refreshAccessToken } from '../api/auth';
import { ApiClientProvider } from './ApiClientContext';

export type User = {
  id: number;
  username: string;
  email: string | null;
  full_name: string | null;
  is_admin: boolean;
  is_active: boolean;
  last_login: string;
  last_seen: string;
};

export type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: User };

type AuthApi = {
  state: AuthState;
  setAuthenticated: (user: User) => void;
  logout: () => Promise<void>;
};

const AuthCtx = createContext<AuthApi | null>(null);

export function useAuth(): AuthApi {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('AuthProvider missing');
  return v;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  // All mutable token state lives in a ref so closure-based callbacks
  // always see the latest value without recreating the client.
  const tokenRef = useRef<string | null>(null);
  const refreshInFlight = useRef<Promise<string | null> | null>(null);

  // Stable callbacks — these never change identity (useCallback with [] / [queryClient]).
  const refresh = useCallback(async () => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const promise = refreshAccessToken().then((res) => {
      if (!res) return null;
      tokenRef.current = res.accessToken;
      return res.accessToken;
    });
    promise.finally(() => {
      refreshInFlight.current = null;
    });
    refreshInFlight.current = promise;
    return promise;
  }, []);

  const onAuthFailure = useCallback(() => {
    tokenRef.current = null;
    queryClient.clear();
    setState({ status: 'anonymous' });
  }, [queryClient]);

  // Create the API client exactly once via lazy useState initializer.
  // The lambdas here only READ tokenRef at request time (inside fetch middleware),
  // never during render — this is a known false positive of react-hooks/refs.
  // eslint-disable-next-line react-hooks/refs -- closures read ref at request-time, not during render
  const [client] = useState(() =>
    createApiClient({
      getToken: () => tokenRef.current,
      refresh,
      onAuthFailure,
    }),
  );

  const $api = useMemo(() => createOpenApiClient(client), [client]);

  const setAuthenticated = useCallback((user: User) => {
    setState({ status: 'authenticated', user });
  }, []);

  const logout = useCallback(async () => {
    try {
      await client.DELETE('/api/v2/auth/token');
    } catch {
      // ignore — cookie is cleared by us regardless
    }
    tokenRef.current = null;
    queryClient.clear();
    setState({ status: 'anonymous' });
    window.location.replace('/ui/?logged_out=1');
  }, [client, queryClient]);

  const api = useMemo<AuthApi>(
    () => ({ state, setAuthenticated, logout }),
    [state, setAuthenticated, logout],
  );

  // Bootstrap: trigger the first /auth/me. Middleware handles refresh-on-401.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await client.GET('/api/v2/auth/me');
      if (cancelled) return;
      if (r.data) setState({ status: 'authenticated', user: r.data as User });
      else if (state.status === 'loading') setState({ status: 'anonymous' });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  return (
    <AuthCtx.Provider value={api}>
      <ApiClientProvider client={client} $api={$api}>
        {children}
      </ApiClientProvider>
    </AuthCtx.Provider>
  );
}
